import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { useAuth, API } from "../lib/auth";
import { Search, MapPin, Briefcase, Loader2, Sparkles, MessageSquare } from "lucide-react";
import { useI18n } from "../lib/i18n";

export default function CandidateDatabase() {
  const { authHeader } = useAuth();
  const { lang } = useI18n();
  const [filters, setFilters] = useState({ q: "", skills: "", location: "", title: "" });
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      const r = await axios.get(`${API}/employer/candidates?${params.toString()}`, { headers: authHeader });
      setCandidates(r.data || []);
    } catch (e) { toast.error("Search failed"); }
    setLoading(false);
  };

  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);

  const openProfile = async (c) => {
    setActive(c);
    // Don't reuse old AI summary if its language differs from current UI
    const cachedLangOk = !c.ai_summary_lang || c.ai_summary_lang === lang;
    setSummary(cachedLangOk ? (c.ai_summary || "") : "");
    if (!c.ai_summary || !cachedLangOk) {
      setSummaryLoading(true);
      try {
        const r = await axios.post(`${API}/employer/candidates/${c.id}/summary?lang=${encodeURIComponent(lang)}`, {}, { headers: authHeader });
        setSummary(r.data.summary || "");
      } catch (e) {}
      setSummaryLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!msgText.trim() || !active) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { to_user_id: active.id, text: msgText, subject: "Motravay - opportunity" }, { headers: authHeader });
      toast.success("Message sent"); setMsgText(""); setMsgOpen(false);
    } catch (e) { toast.error("Send failed"); }
    setSending(false);
  };

  // Group by title (category)
  const grouped = candidates.reduce((acc, c) => {
    const key = c.title || "Unspecified";
    (acc[key] = acc[key] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="candidate-database">
      <Card className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2 flex items-center gap-2 bg-slate-50 rounded-lg px-3 border border-slate-200">
            <Search className="h-4 w-4 text-slate-400" />
            <Input data-testid="cdb-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Name, email, anything" className="border-0 focus-visible:ring-0 shadow-none h-10" />
          </div>
          <Input data-testid="cdb-title" placeholder="Job title" value={filters.title} onChange={(e) => setFilters({ ...filters, title: e.target.value })} />
          <Input data-testid="cdb-skills" placeholder="Skills" value={filters.skills} onChange={(e) => setFilters({ ...filters, skills: e.target.value })} />
          <Input data-testid="cdb-location" placeholder="Location" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} />
        </div>
        <Button onClick={search} data-testid="cdb-search" disabled={loading} className="mt-3 bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search candidates"}
        </Button>
      </Card>

      {Object.keys(grouped).length === 0 ? (
        <Card className="p-12 text-center text-slate-500">No candidates match your filters.</Card>
      ) : Object.entries(grouped).map(([cat, list]) => (
        <div key={cat}>
          <h3 className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold mb-3">{cat} · {list.length}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map(c => (
              <Card key={c.id} className="p-5 hover:border-[#1A8FE3] transition-colors cursor-pointer" data-testid={`candidate-card-${c.id}`} onClick={() => openProfile(c)}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-slate-900">{c.name || "Anonymous"}</div>
                    {c.title && <div className="text-xs text-slate-500 mt-0.5">{c.title}</div>}
                  </div>
                  <Sparkles className="h-4 w-4 text-[#1A8FE3]" />
                </div>
                <div className="mt-3 text-xs text-slate-500 space-y-1">
                  {c.location && <div className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{c.location}</div>}
                  {c.skills && <div className="line-clamp-2">{c.skills}</div>}
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {/* Profile dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="candidate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{active?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-600">
              {active?.title && <span className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3" />{active.title}</span>}
              {active?.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{active.location}</span>}
            </div>
            <Card className="p-4 bg-sky-50 border-sky-100">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#1A8FE3] font-semibold mb-1"><Sparkles className="h-3 w-3" />Summary</div>
              {summaryLoading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : <p className="text-slate-800">{summary || "Not enough profile data to summarise."}</p>}
            </Card>
            {active?.experience_summary && (<div><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Experience</div><p className="text-slate-700 whitespace-pre-line">{active.experience_summary}</p></div>)}
            {active?.work_history && (<div><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Work history</div><p className="text-slate-700 whitespace-pre-line">{active.work_history}</p></div>)}
            {active?.education && (<div><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Education</div><p className="text-slate-700 whitespace-pre-line">{active.education}</p></div>)}
            {active?.skills && (<div><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Skills</div><p className="text-slate-700">{active.skills}</p></div>)}
            <Button onClick={() => setMsgOpen(true)} data-testid="cdb-message-btn" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
              <MessageSquare className="h-4 w-4 mr-2" />Message candidate
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Compose dialog */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent data-testid="msg-dialog">
          <DialogHeader><DialogTitle>Message {active?.name}</DialogTitle></DialogHeader>
          <Textarea data-testid="cdb-msg-text" rows={6} value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Introduce your opportunity…" />
          <Button onClick={sendMessage} disabled={sending || !msgText.trim()} data-testid="cdb-msg-send" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
